import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import Grid from '~/components/core/Atomic/Grid/Grid';
import useFormikChangeListener from '~/components/core/Formik/FormikChangeListener';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import CONFIGURABLE_LOBS from '~/server_shared/generated-types/CONFIGURABLE_LOBS';

import CheckboxFormik from '../../../../CheckboxFormik';
import FsTooltip from '../../../../core/FsWrappers/FsTooltip/FsTooltip';
import TextFieldFormik from '../../../../TextFieldFormik';
import copies from '../copies.json';
import FieldConfigurationCard from '../FieldConfigurationCard/FieldConfigurationCard';

import BaseConfigurationSection from './BaseConfigurationSection';
import ConfiguredFieldsSection from './ConfiguredFieldsSection';

const ID_PREFIX = 'configuration.incident_details.pre_defined_fields';
const TEXTS = copies.additional_information;
const KEY = 'supplementary_information';
const SECTION_ID = `${ID_PREFIX}.${KEY}`;
// Same registry entry the FNOL section, its submit maps and the Create Like draft builder read, so
// the ids configurable here are the ids those consumers know about. Card render order follows it.
const AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION =
  CONFIGURABLE_LOBS.SUPPLEMENTARY_INFORMATION_FIELDS.lob_specific.auto_claim;
const AUTO_SPECIFIC_ADDITIONAL_PATH = AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION.config_group;
const SUPPLEMENTARY_BASE_FIELDS = CONFIGURABLE_LOBS.SUPPLEMENTARY_INFORMATION_FIELDS.base;
const AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION_FIELDS = AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION.fields;

const isMandatoryFieldsExists = (values) =>
  values?.lob === 'auto_claim'
    ? isSomeFieldsMandatory(values) || isSomeAutoSpecificFieldsMandatory(values)
    : isSomeFieldsMandatory(values);

const isSomeFieldsMandatory = (values) =>
  SUPPLEMENTARY_BASE_FIELDS.some((fieldId) => get(values, `${ID_PREFIX}.${fieldId}.mandatory`, false));

const isSomeAutoSpecificFieldsMandatory = (values) =>
  AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION_FIELDS.some((fieldId) =>
    get(values, `${ID_PREFIX}.${AUTO_SPECIFIC_ADDITIONAL_PATH}.${fieldId}.mandatory`, false)
  );

const SupplementaryInformationSection = () => {
  const { values, setFieldValue } = useFormikContext();
  const lob = get(values, 'lob');
  const title = get(values, `${SECTION_ID}.desc`);
  const [isHidden, setIsHidden] = useState(false);
  const isAutoLob = lob === 'auto_claim';

  // Enforce all supplementary section to be hidden if the section is show is set to false
  useFormikChangeListener({
    listenForKeys: [`${SECTION_ID}.active`],
    onChange: (values, _) => {
      !get(values, `${SECTION_ID}.active`, false) ? setIsHidden(true) : setIsHidden(false);
    },
    runOnFirstRender: true,
  });

  // Enforce the section to be mandatory if any of the supplementary fields are mandatory
  useFormikChangeListener({
    listenForKeys: SUPPLEMENTARY_BASE_FIELDS.map((fieldId) => `${ID_PREFIX}.${fieldId}.mandatory`),
    onChange: (values, _) => {
      if (isMandatoryFieldsExists(values)) {
        setFieldValue(`${SECTION_ID}.mandatory`, true);
      }
    },
    runOnFirstRender: true,
  });

  return (
    <BaseConfigurationSection title={title} subtitle={`Key: ${KEY}`} isHidden={isHidden} isSection>
      <Grid item xs={6}>
        <TextFieldFormik id={`${SECTION_ID}.desc`} label="Display Name" fullWidth />
      </Grid>
      <Grid item xs={6}>
        <TextFieldFormik id={SECTION_ID} value={KEY} label="Key" fullWidth disabled />
      </Grid>
      <Grid item xs={12}>
        <Grid container className="flex items-center">
          <Grid item xs={2}>
            <SwitchFormik id={`${SECTION_ID}.active`} label="Show" hideErrorGap />
          </Grid>
          <Grid item xs={10}>
            {isMandatoryFieldsExists(values) ? (
              <FsTooltip arrow title={TEXTS.required_when_field_mandatory_tooltip}>
                <CheckboxFormik id={`${SECTION_ID}.mandatory`} label={TEXTS.required_label} disabled />
              </FsTooltip>
            ) : (
              // Required on a section with no required field inside it gates on acknowledgement, not on
              // content, so say so here rather than leaving the admin to infer it from the label.
              <FsTooltip arrow title={TEXTS.required_acknowledgement_tooltip}>
                <CheckboxFormik
                  id={`${SECTION_ID}.mandatory`}
                  label={TEXTS.required_label}
                  onChange={() =>
                    setFieldValue(`${SECTION_ID}.mandatory`, !get(values, `${SECTION_ID}.mandatory`, false))
                  }
                />
              </FsTooltip>
            )}
          </Grid>
        </Grid>
      </Grid>
      {SUPPLEMENTARY_BASE_FIELDS.map((fieldId) => (
        <FieldConfigurationCard key={fieldId} idPrefix={ID_PREFIX} fieldId={fieldId} disabledCollapse={isHidden} />
      ))}
      {isAutoLob &&
        AUTO_SPECIFIC_SUPPLEMENTARY_INFORMATION_FIELDS.map((fieldId) => (
          <FieldConfigurationCard
            key={fieldId}
            idPrefix={`${ID_PREFIX}.${AUTO_SPECIFIC_ADDITIONAL_PATH}`}
            fieldId={fieldId}
            disabledCollapse={isHidden}
          />
        ))}
      <ConfiguredFieldsSection
        fieldId="configuration.incident_details.configured_fields"
        section="supplementary_info"
        disabled={isHidden}
        withDndReorder
      />
    </BaseConfigurationSection>
  );
};

export default SupplementaryInformationSection;
